/**
 * rowQualityCopy (#2773 C) — the German copy and the per-class arithmetic for the
 * row classifier. One module, for the same reason ``estimateMark`` exists: the
 * summary note, the table chips and the drawer's reason list all describe the SAME
 * classification, and a fourth surface must not be able to invent its own word for it.
 *
 * **#2948 §4 retired the hide-rows mechanism.** The product call this module used to
 * carry — "every non-``ok`` row is hidden by default, revealed by a toggle" — is gone:
 * every Kultur in the period now renders, always, and the reason chips are the SOLE
 * mechanism marking a row as incomplete / not applicable / not trustworthy. Hiding a row
 * on top of chipping it was a second, redundant answer to the same question. With it went
 * ``toggleLabel`` and the per-class ``toggle:`` strings.
 *
 * **HOF-20 then retired the page-head hero itself**, and the per-class arithmetic that
 * served it went with it: ``EXCLUDED_CLASSES``, ``ExcludedCounts``, ``countExcluded`` and
 * ``excludedNote`` existed solely to write the hero's „nicht mitgerechnet"-subline — the
 * apology for a Σ that counted ``ok`` rows while the table below showed every Kultur. With
 * no Σ there is no gap to apologise for, so the module is now purely the CHIP copy: the
 * reason strings, their grain-honest explanations, and the two entry points that build a
 * row's or a group's chips.
 *
 * ``not_applicable`` gets its OWN words throughout — non-marketable land is
 * definitionally without a Deckungsbeitrag, and calling it "unvollständig"
 * would report a data gap where there is none.
 */
import { ESTIMATED_LABEL, ESTIMATED_TITLE } from './estimateMark';
import {
  REASON_ORDER,
  classifyRow,
  type RowQuality,
  type RowQualityReason,
  type RowQualitySignals,
} from './rowQuality';
import { type ValuationRefusal } from './types';

/**
 * Which identity a chip's explanation is about — one Schlag, one Hauptschlag analysis
 * unit (HOF-195), or one Kultur+Sorte group.
 *
 * ``einheit`` is a THIRD grain, not a synonym for either neighbour, and its strings say
 * which: a ``some()``-propagated reason on a unit reads „mindestens ein Block dieser
 * Analyseeinheit …" (the #2809 rule, one level down), while the three reasons that are
 * genuinely RE-DERIVED at unit grain — ``per_basis_implausible``, ``thin_source``,
 * ``cost_coverage_low`` — speak about the unit itself. Those three degrade to the
 * ``some()`` fold where the evidence cannot decide, so their sentences are written to
 * hold under BOTH paths: they never claim which grain reached the verdict, only that it
 * is true of the unit or of one of its blocks. Over-claiming there would be #2809 coming
 * back through the one door the re-derivation opens.
 */
export type ReasonGrain = 'schlag' | 'kultur' | 'einheit';

export interface ReasonCopy {
  /** The chip word. Short, lowercase-ish, readable at a glance. */
  label: string;
  /**
   * The hover explanation, per grain. Kultur-grain strings for ``some()``-
   * propagated reasons MUST say "mindestens ein Schlag" (#2809): the group flag
   * does not claim every member, and copy that over-claims is the exact bug
   * ``ESTIMATED_TITLE`` was split three ways to avoid.
   */
  title: Record<ReasonGrain, string>;
}

/**
 * One German string per reason token. Exhaustive by TYPE (``Record`` over the
 * union), so a reason added to ``rowQuality.ts`` cannot reach the screen
 * unlabelled — ``tsc`` finds it here first.
 */
export const REASON_COPY: Record<RowQualityReason, ReasonCopy> = {
  no_revenue_booked: {
    label: 'keine Erlöse gebucht',
    title: {
      // Sums grain at Kultur level (one booked member un-pends the group), so
      // "diese Kultur" is exact — no "mindestens ein" hedge needed.
      schlag: 'Für diesen Schlag sind Kosten gebucht, aber noch keine Erlöse — die Zahl ist unvollständig, nicht falsch.',
      kultur: 'Für diese Kultur sind Kosten gebucht, aber noch keine Erlöse — die Zahl ist unvollständig, nicht falsch.',
      // Sums grain at unit level too (one booked block un-pends the unit) — no hedge needed.
      einheit:
        'Für diese Analyseeinheit sind Kosten gebucht, aber noch keine Erlöse — die Zahl ist unvollständig, nicht falsch.',
    },
  },
  not_marketable: {
    label: 'ohne Deckungsbeitrag',
    title: {
      // every() grain — constant within a group by construction, so "diese Kultur" holds.
      schlag:
        'Diese Fläche wird nicht vermarktet (z. B. Grünland oder Brache) — ein Deckungsbeitrag ist dafür nicht definiert.',
      kultur:
        'Diese Kultur wird nicht vermarktet (z. B. Grünland oder Brache) — ein Deckungsbeitrag ist dafür nicht definiert.',
      // every() grain at unit level too, so „diese Analyseeinheit" is exact.
      einheit:
        'Diese Analyseeinheit wird nicht vermarktet (z. B. Grünland oder Brache) — ein Deckungsbeitrag ist dafür nicht definiert.',
    },
  },
  // Estimate-shaped: the word and the explanation come STRAIGHT from the shared
  // estimateMark module, so the drawer chip and the hero pill say the same thing.
  leistung_modelled: {
    label: ESTIMATED_LABEL,
    title: { schlag: ESTIMATED_TITLE.schlag, kultur: ESTIMATED_TITLE.kultur, einheit: ESTIMATED_TITLE.einheit },
  },
  required_cost_bucket_missing: {
    label: 'Kosten fehlen',
    title: {
      schlag:
        'Für diesen Schlag steht Saatgut/Pflanzgut oder Lohn auf 0,00 € — der Deckungsbeitrag fällt dadurch zu hoch aus.',
      kultur:
        'Für mindestens einen Schlag dieser Kultur steht Saatgut/Pflanzgut oder Lohn auf 0,00 € — der Deckungsbeitrag fällt dadurch zu hoch aus.',
      // some() at unit grain — deliberately NOT recomputed over the Σ buckets: a sibling
      // block that booked its Lohn must not vouch for one whose bucket is empty.
      //
      // #4172 — „Lohn" and not „Löhne": since wave 2 the wage lives across two lines
      // (Pflücklohn and Löhne) and the signal fires only when BOTH are empty, so naming one
      // of them would send a farmer to check a line that is allowed to be 0.
      einheit:
        'Für mindestens einen Block dieser Analyseeinheit steht Saatgut/Pflanzgut oder Lohn auf 0,00 € — der Deckungsbeitrag fällt dadurch zu hoch aus.',
    },
  },
  denominator_untrusted: {
    label: 'Bezugsgröße unklar',
    title: {
      schlag:
        'Für diesen Schlag fehlt die Fläche oder die Bestandsdichte ist nicht belegt — je-Bezugsgröße-Werte lassen sich daraus nicht verlässlich rechnen.',
      kultur:
        'Für mindestens einen Schlag dieser Kultur fehlt die Fläche oder die Bestandsdichte ist nicht belegt — je-Bezugsgröße-Werte lassen sich daraus nicht verlässlich rechnen.',
      einheit:
        'Für mindestens einen Block dieser Analyseeinheit fehlt die Fläche oder die Bestandsdichte ist nicht belegt — je-Bezugsgröße-Werte lassen sich daraus nicht verlässlich rechnen.',
    },
  },
  per_basis_implausible: {
    label: 'unplausibel',
    title: {
      // 2026-10-06 — the worker judges DB1 je kg and je Pflanze, no longer je Hektar (a
      // hectare of Stellage tunnel and a hectare of open field are not the same unit).
      // The sentence names the two it reads, so nobody is sent to the je-Hektar column.
      schlag:
        'Der Deckungsbeitrag je Kilogramm oder je Pflanze liegt außerhalb des für diese Kultur hinterlegten Erwartungsbereichs.',
      kultur:
        'Für mindestens einen Schlag dieser Kultur liegt der Deckungsbeitrag je Kilogramm oder je Pflanze außerhalb des hinterlegten Erwartungsbereichs.',
      // HOF-195 — re-judged on the unit's OWN denominator, degrading to the block verdict
      // where no unit denominator exists. The sentence holds under both paths and claims
      // neither: what it does claim is that the chip is not merely inherited from a block
      // whose 0,44-ha denominator the farmer already told us not to believe.
      einheit:
        'Für diese Analyseeinheit oder für mindestens einen ihrer Blöcke liegt der Deckungsbeitrag je Kilogramm oder je Pflanze außerhalb des hinterlegten Erwartungsbereichs.',
    },
  },
  thin_source: {
    label: 'wenige Belege',
    title: {
      schlag:
        'Dieser Deckungsbeitrag stammt aus Kategorien-Summen oder aus sehr wenigen Einzelbuchungen — es steckt wenig Beleg dahinter.',
      kultur:
        'Für mindestens einen Schlag dieser Kultur stammen die Zahlen aus Kategorien-Summen oder aus sehr wenigen Einzelbuchungen.',
      // HOF-195 — the line-count arm IS recomputed over the Σ; the Kategorien-Summen arm
      // keeps the some() fold, and the sentence separates the two rather than blurring them.
      einheit:
        'Für diese Analyseeinheit stammen die Zahlen aus Kategorien-Summen oder — über alle Blöcke zusammen — aus sehr wenigen Einzelbuchungen.',
    },
  },
  // #2871 — two corrections to the #2773 C wording, both about what this flag can claim.
  // Verdict + evidence: ``sessions/tasks/release-31.11/14-2871-entscheidung.md``.
  //
  // 1. "hinterlegt", not "zugeordnet". "zugeordnet" (attributed) names an ATTRIBUTION
  //    step, which reads as "HofSync has not assigned your costs yet" and points the
  //    farmer at the wrong party — for WF the costs are absent from the source entirely
  //    (no money column in any of the 10 MyFarm24 field-record-card sections, all 8
  //    export years). "hinterlegt" states the DATA STATE and stays true whoever owns the
  //    hole, which matters because this reason token is FLEET-WIDE: on a tenant whose
  //    bank-side costs exist but are dropped by the DROP-not-MERGE contract
  //    (``deckungsbeitrag.py:1606``), the gap really is on our side. Deliberately NOT
  //    "fehlen in den Quelldaten" — that asserts source-side absence, which is a
  //    WF-specific measurement and would be false on exactly that tenant.
  // 2. "Arbeitsgänge", not "Kosten". ``measure_cost_coverage`` is ops_costed / ops_total —
  //    a share of the cycle's OPERATION population, not a € share, and it is amount-blind
  //    (one cent marks an operation costed). "vielen" rather than "den meisten" carries no
  //    numeric claim: "most" would only hold at the DEFAULT 0.5 warn ratio, and the
  //    threshold is env-tunable (``DECKUNGSBEITRAG_COST_COVERAGE_WARN_RATIO``).
  cost_coverage_low: {
    label: 'Kostenbasis dünn',
    title: {
      schlag:
        'Für diesen Schlag sind zu vielen Arbeitsgängen keine Kosten hinterlegt — der Deckungsbeitrag fällt dadurch zu hoch aus.',
      kultur:
        'Für mindestens einen Schlag dieser Kultur sind zu vielen Arbeitsgängen keine Kosten hinterlegt — der Deckungsbeitrag fällt dadurch zu hoch aus.',
      // HOF-195 — a plain some() fold at unit grain, so the sentence hedges over the
      // blocks. The Σ recompute the matrix originally wanted is not takeable: the worker
      // emits the coverage counts on FLAGGED rows only, so an unflagged block carries no
      // denominator to add. See ``rowQuality.ts``, above ``unitThinSource``.
      einheit:
        'Für mindestens einen Block dieser Analyseeinheit sind zu vielen Arbeitsgängen keine Kosten hinterlegt — der Deckungsbeitrag fällt dadurch zu hoch aus.',
    },
  },
  // HOF-102 D2 — the Leistung side's „Kosten fehlen". Same shape of claim (a bucket is
  // knowingly short), so deliberately the same shape of sentence, in the opposite
  // direction: an unpriced Menge makes the Deckungsbeitrag too LOW.
  //
  // „Menge ohne Preis", not „nicht bewertet": the per-Posten chip in the drawer owns that
  // phrase, and the row-grain claim is different — the row IS valued, part of its harvest
  // just had nothing to be valued at.
  leistung_unpriced: {
    label: 'Menge ohne Preis',
    title: {
      schlag:
        'Für einen Teil der geernteten Menge liegt im Preisspiegel kein Monatspreis vor — sie steht mit 0,00 € ' +
        'in der Leistung, der Deckungsbeitrag fällt dadurch zu niedrig aus.',
      kultur:
        'Für mindestens einen Schlag dieser Kultur liegt für einen Teil der geernteten Menge kein Monatspreis ' +
        'vor — sie steht mit 0,00 € in der Leistung, der Deckungsbeitrag fällt dadurch zu niedrig aus.',
      einheit:
        'Für mindestens einen Block dieser Analyseeinheit liegt für einen Teil der geernteten Menge kein ' +
        'Monatspreis vor — sie steht mit 0,00 € in der Leistung, der Deckungsbeitrag fällt dadurch zu niedrig aus.',
    },
  },
};

/**
 * HOF-140 — the row-grain explanation per REFUSAL, for the one reason whose cause the
 * fact names: ``leistung_unpriced``.
 *
 * **Why this exists.** ``REASON_COPY.leistung_unpriced.title`` is one sentence per grain
 * and it hardcodes the ``no_price`` explanation — *„im Preisspiegel liegt kein
 * Monatspreis vor"*. The worker's refusal vocabulary is a CLOSED set (`no_price` /
 * `incomplete` / `not_netto` / `mixed_unit` / `multi_item` / `no_catalog_item` /
 * `no_month` / `no_quantity` / `unconvertible_unit`) designed exactly so the FE can say
 * the right thing about each, and ``REASON_INCOMPLETE``'s own comment in
 * ``leistung_valuation.py`` writes the brief: *"the prices are neither missing nor
 * contradictory, they are incomplete, and the farmer's question — 'why is this Posten
 * not valued' — has a third answer."* WF's June 2026 Erdbeeren were refused ``incomplete``
 * in a month holding 2.336 usable ``verkauf`` observations, and the chip told the farmer
 * the Preisspiegel had no price for it. That is a false sentence about a real month.
 *
 * **The LABEL is deliberately not split.** „Menge ohne Preis" is true of every refusal
 * and is the two-word class name the table column is scanned for; nine words in one
 * column is a column nobody reads. Only the hover explanation varies.
 *
 * **The diagnosis is the per-Posten one, one grain up.** Every string here is the
 * row-grain form of the matching ``REFUSAL_NOTE`` in ``leistungMark`` — same cause, same
 * consequence, different subject („dieser Schlag" / „mindestens ein Schlag dieser
 * Kultur" rather than „diese Buchung") — so the drawer's per-Posten chip and the row
 * chip above it can never diagnose the same refusal two ways.
 *
 * **Kultur grain always hedges** (#2809). ``leistung_unpriced`` propagates to a group
 * with ``some()``, so one short Schlag flags the whole Kultur: no group-grain sentence
 * may speak of the Kultur as a whole, not even where the cause happens to be
 * crop-constant (``multi_item`` / ``no_catalog_item``). A copy rule with a per-token
 * exception is how #2809 came back the first time.
 *
 * **Every sentence ends on the consequence** — the Menge is in the Leistung at 0,00 €
 * and the Deckungsbeitrag is therefore too LOW — with one exception. ``no_quantity``
 * may not claim the direction: with no Menge recorded there is no quantity we know went
 * unvalued. ``REFUSAL_NOTE`` makes exactly the same exception, for the same reason. (It
 * is also unreachable today: ``value_leistung_at_market`` selects Posten on
 * ``_harvest_qty(op) > 0``. Written out anyway, because the record is exhaustive by
 * TYPE and a gate that moves must not silently hand a farmer another refusal's sentence.)
 */
export const UNPRICED_REFUSAL_TITLE: Record<ValuationRefusal, Record<ReasonGrain, string>> = {
  // The one refusal today's copy was WRITTEN for, so it is the same object rather than a
  // second copy of the same sentence. It is also the fallback ``chipsFor`` keeps for a
  // MIXED row — which reason wins there is a product-copy decision with three defensible
  // answers (most severe / most frequent / a compound sentence), carved out of train 15
  // and Antonio's to make. Whoever implements that answer is the one who has to split
  // these two apart.
  no_price: REASON_COPY.leistung_unpriced.title,
  incomplete: {
    schlag:
      'Für einen Teil der geernteten Menge ließ sich der Monatsdurchschnitt nicht bilden — bei einzelnen ' +
      'Verkäufen dieses Monats fehlen Betrag oder Menge, und ein Durchschnitt aus dem Rest wäre nicht der ' +
      'Monatspreis. Die Menge steht mit 0,00 € in der Leistung, der Deckungsbeitrag fällt dadurch zu niedrig aus.',
    kultur:
      'Für mindestens einen Schlag dieser Kultur ließ sich der Monatsdurchschnitt nicht bilden — bei einzelnen ' +
      'Verkäufen des Monats fehlen Betrag oder Menge, und ein Durchschnitt aus dem Rest wäre nicht der ' +
      'Monatspreis. Die Menge steht mit 0,00 € in der Leistung, der Deckungsbeitrag fällt dadurch zu niedrig aus.',
    einheit:
      'Für mindestens einen Block dieser Analyseeinheit ließ sich der Monatsdurchschnitt nicht bilden — bei ' +
      'einzelnen Verkäufen des Monats fehlen Betrag oder Menge, und ein Durchschnitt aus dem Rest wäre nicht der ' +
      'Monatspreis. Die Menge steht mit 0,00 € in der Leistung, der Deckungsbeitrag fällt dadurch zu niedrig aus.',
  },
  not_netto: {
    schlag:
      'Für einen Teil der geernteten Menge sind die Preise dieses Monats ausdrücklich als Bruttopreise erfasst, ' +
      'der Deckungsbeitrag rechnet aber netto. Die Menge steht mit 0,00 € in der Leistung, der Deckungsbeitrag ' +
      'fällt dadurch zu niedrig aus.',
    kultur:
      'Für mindestens einen Schlag dieser Kultur sind die Preise des betreffenden Monats ausdrücklich als ' +
      'Bruttopreise erfasst, der Deckungsbeitrag rechnet aber netto. Die Menge steht mit 0,00 € in der Leistung, ' +
      'der Deckungsbeitrag fällt dadurch zu niedrig aus.',
    einheit:
      'Für mindestens einen Block dieser Analyseeinheit sind die Preise des betreffenden Monats ausdrücklich als ' +
      'Bruttopreise erfasst, der Deckungsbeitrag rechnet aber netto. Die Menge steht mit 0,00 € in der Leistung, ' +
      'der Deckungsbeitrag fällt dadurch zu niedrig aus.',
  },
  mixed_unit: {
    schlag:
      'Für einen Teil der geernteten Menge liegen die Preise dieses Monats in zwei verschiedenen Einheiten vor — ' +
      'ein Mischpreis daraus wäre keine gültige Zahl. Die Menge steht mit 0,00 € in der Leistung, der ' +
      'Deckungsbeitrag fällt dadurch zu niedrig aus.',
    kultur:
      'Für mindestens einen Schlag dieser Kultur liegen die Preise des betreffenden Monats in zwei verschiedenen ' +
      'Einheiten vor — ein Mischpreis daraus wäre keine gültige Zahl. Die Menge steht mit 0,00 € in der Leistung, ' +
      'der Deckungsbeitrag fällt dadurch zu niedrig aus.',
    einheit:
      'Für mindestens einen Block dieser Analyseeinheit liegen die Preise des betreffenden Monats in zwei ' +
      'verschiedenen Einheiten vor — ein Mischpreis daraus wäre keine gültige Zahl. Die Menge steht mit 0,00 € ' +
      'in der Leistung, der Deckungsbeitrag fällt dadurch zu niedrig aus.',
  },
  multi_item: {
    schlag:
      'Diese Kultur wird im Preisspiegel unter mehreren Artikeln geführt (z. B. Spargel weiß/grün), und welcher ' +
      'hier gilt, ist nicht belegt. Die geerntete Menge steht mit 0,00 € in der Leistung, der Deckungsbeitrag ' +
      'fällt dadurch zu niedrig aus.',
    kultur:
      'Diese Kultur wird im Preisspiegel unter mehreren Artikeln geführt (z. B. Spargel weiß/grün), und welcher ' +
      'hier gilt, ist nicht belegt. Für mindestens einen Schlag dieser Kultur steht die geerntete Menge deshalb ' +
      'mit 0,00 € in der Leistung, der Deckungsbeitrag fällt dadurch zu niedrig aus.',
    einheit:
      'Diese Kultur wird im Preisspiegel unter mehreren Artikeln geführt (z. B. Spargel weiß/grün), und welcher ' +
      'hier gilt, ist nicht belegt. Für mindestens einen Block dieser Analyseeinheit steht die geerntete Menge ' +
      'deshalb mit 0,00 € in der Leistung, der Deckungsbeitrag fällt dadurch zu niedrig aus.',
  },
  no_catalog_item: {
    schlag:
      'Für diese Kultur gibt es keinen Preisspiegel-Artikel, an dem die geerntete Menge bewertet werden könnte. ' +
      'Sie steht mit 0,00 € in der Leistung, der Deckungsbeitrag fällt dadurch zu niedrig aus.',
    kultur:
      'Für diese Kultur gibt es keinen Preisspiegel-Artikel, an dem die geerntete Menge bewertet werden könnte. ' +
      'Für mindestens einen Schlag dieser Kultur steht sie deshalb mit 0,00 € in der Leistung, der ' +
      'Deckungsbeitrag fällt dadurch zu niedrig aus.',
    einheit:
      'Für diese Kultur gibt es keinen Preisspiegel-Artikel, an dem die geerntete Menge bewertet werden könnte. ' +
      'Für mindestens einen Block dieser Analyseeinheit steht sie deshalb mit 0,00 € in der Leistung, der ' +
      'Deckungsbeitrag fällt dadurch zu niedrig aus.',
  },
  no_month: {
    schlag:
      'Ein Teil der Erntebuchungen dieses Schlags trägt kein verwertbares Datum und lässt sich deshalb keinem ' +
      'Monatspreis zuordnen. Die Menge steht mit 0,00 € in der Leistung, der Deckungsbeitrag fällt dadurch zu ' +
      'niedrig aus.',
    kultur:
      'Für mindestens einen Schlag dieser Kultur tragen Erntebuchungen kein verwertbares Datum und lassen sich ' +
      'deshalb keinem Monatspreis zuordnen. Die Menge steht mit 0,00 € in der Leistung, der Deckungsbeitrag ' +
      'fällt dadurch zu niedrig aus.',
    einheit:
      'Für mindestens einen Block dieser Analyseeinheit tragen Erntebuchungen kein verwertbares Datum und lassen ' +
      'sich deshalb keinem Monatspreis zuordnen. Die Menge steht mit 0,00 € in der Leistung, der Deckungsbeitrag ' +
      'fällt dadurch zu niedrig aus.',
  },
  no_quantity: {
    // No direction claim — see the module note above.
    schlag:
      'Zu einem Teil der Erntebuchungen dieses Schlags ist keine Menge erfasst, die bewertet werden könnte — sie ' +
      'stehen mit 0,00 € in der Leistung.',
    kultur:
      'Für mindestens einen Schlag dieser Kultur ist zu Erntebuchungen keine Menge erfasst, die bewertet werden ' +
      'könnte — sie stehen mit 0,00 € in der Leistung.',
    // No direction claim here either — the module note's one exception, at every grain.
    einheit:
      'Für mindestens einen Block dieser Analyseeinheit ist zu Erntebuchungen keine Menge erfasst, die bewertet ' +
      'werden könnte — sie stehen mit 0,00 € in der Leistung.',
  },
  unconvertible_unit: {
    schlag:
      'Für einen Teil der geernteten Menge lässt sich die erfasste Mengeneinheit nicht in die Preiseinheit ' +
      'umrechnen. Die Menge steht mit 0,00 € in der Leistung, der Deckungsbeitrag fällt dadurch zu niedrig aus.',
    kultur:
      'Für mindestens einen Schlag dieser Kultur lässt sich die erfasste Mengeneinheit nicht in die Preiseinheit ' +
      'umrechnen. Die Menge steht mit 0,00 € in der Leistung, der Deckungsbeitrag fällt dadurch zu niedrig aus.',
    einheit:
      'Für mindestens einen Block dieser Analyseeinheit lässt sich die erfasste Mengeneinheit nicht in die ' +
      'Preiseinheit umrechnen. Die Menge steht mit 0,00 € in der Leistung, der Deckungsbeitrag fällt dadurch zu ' +
      'niedrig aus.',
  },
};

export interface ReasonChip {
  reason: RowQualityReason;
  label: string;
  title: string;
}

/**
 * The chips a REVEALED row renders, in ``REASON_ORDER``.
 *
 * ``leistung_modelled`` is deliberately excluded: it is a marker-only reason
 * that never hides a row and already has its own long-standing surface
 * (``EstimatedChip``, #2760/#2780) on the very same table cell. Emitting it here
 * too would put two pills on one row for one fact — the drift ``estimateMark``
 * was created to stop.
 */
export function reasonChips(row: RowQualitySignals): ReasonChip[] {
  return chipsFor(classifyRow(row), 'schlag');
}

/**
 * Shared tail — the grain decides which explanation the chip carries (#2809).
 *
 * Takes the whole ``RowQuality``, not just ``reasons[]``, because the STATE is the gate:
 * an ``ok`` row renders no chips even when it carries reasons. That case is real, not
 * theoretical — ``stateOf`` only claims ``untrustworthy`` for rows with ``leistung > 0``,
 * so an all-zero cycle with hollow buckets stays ``ok`` (deliberately visible, per the
 * rollup's all-zero exception) while carrying ``required_cost_bucket_missing``. Chipping
 * it would put "Warum dieser Deckungsbeitrag nicht in der Übersicht steht" on a row that IS in the
 * overview and is counted in no hidden class — a flat contradiction of the subline.
 *
 * Two reasons are then suppressed rather than rendered:
 *
 * - ``leistung_modelled`` — marker-only, and already rendered by ``EstimatedChip`` /
 *   ``LeistungEstimateNote`` on the very same surfaces.
 * - ``no_revenue_booked`` ON a ``not_marketable`` row. Grünland trips the revenueless
 *   predicate by construction (leistung 0, costs booked), but "noch keine Erlöse gebucht
 *   — die Zahl ist unvollständig" is FALSE about it: nothing is missing, the land is
 *   simply not sold. The classifier's precedence already resolves the STATE to
 *   ``not_applicable``; this keeps the COPY from contradicting it, which is the
 *   acceptance criterion "not_applicable is never lumped into the incomplete wording".
 */
export function chipsFor(quality: RowQuality, grain: ReasonGrain): ReasonChip[] {
  if (quality.state === 'ok') return [];
  const reasons = quality.reasons;
  const notMarketable = reasons.includes('not_marketable');
  return REASON_ORDER.filter(
    (reason) =>
      reason !== 'leistung_modelled' &&
      !(notMarketable && reason === 'no_revenue_booked') &&
      reasons.includes(reason),
  ).map((reason) => ({
    reason,
    label: REASON_COPY[reason].label,
    title: titleFor(reason, grain, quality.unpricedReason),
  }));
}

/**
 * HOF-140 — the chip's explanation, refined by the row's OWN refusal where the fact names
 * one.
 *
 * The fallback is not a default in disguise: ``unpricedReason`` is absent exactly when
 * the grain has no single answer (mixed Posten, or no readable ``leistung_posten``
 * block), and there the wording stays what it is today until Antonio decides what a mixed
 * row should say. Every other reason is untouched — this refines one token, not the copy
 * module.
 */
function titleFor(
  reason: RowQualityReason,
  grain: ReasonGrain,
  unpricedReason: ValuationRefusal | undefined,
): string {
  if (reason === 'leistung_unpriced' && unpricedReason !== undefined) {
    return UNPRICED_REFUSAL_TITLE[unpricedReason][grain];
  }
  return REASON_COPY[reason].title[grain];
}
